import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import {
  useGetAbandonmentReportQuery,
  useGetCampaignsQuery,
  useGetDialerReportQuery,
  useGetDialerSessionHistoryQuery,
  useGetHomeQuery,
  useGetMembersQuery,
  useGetOwnPhoneNumbersQuery,
} from "@/store/rest_api";
import type { DialerReportFilter, DialerReportRow } from "@/model/interface/DialerSession";
import { CampaignTargetType } from "@/enum/CampaignTargetType";
import { CampaignType } from "@/enum/CampaignType";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { AUDIENCE_SOURCE_LABELS, HAND_PICKED_AUDIENCE } from "@/util/campaignAudience";
import { calendarDateIn, formatCalendarDate, formatDateTime, formatHoursMinutes } from "@/util/date";
import { formatAbandonRate } from "@/util/dialer";
import { downloadCsv } from "@/util/csv";
import { memberName } from "@/util/member";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ALL = "ALL";

const DEFAULT_RANGE_DAYS = 6;

const REPORT_HEADERS = [
  "Agent",
  "Dials",
  "Live answers",
  "Connects",
  "Conversations",
  "Not connected",
  "Neutral",
  "No disposition",
  "Talk time",
  "Wrap-up",
  "Break",
  "Calls per hour",
  "Voicemails",
  "Median connect (s)",
  "Abandoned",
  "Abandonment rate",
];

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toLocaleDateString("en-CA");
}

function optional(value: string): string | undefined {
  return value === ALL ? undefined : value;
}

function reportCells(row: DialerReportRow): (string | number)[] {
  return [
    row.agentName,
    row.dials,
    row.liveAnswers,
    row.connects,
    row.conversations,
    row.connectsByOutcome.NOT_CONNECTED ?? 0,
    row.connectsByOutcome.NEUTRAL ?? 0,
    row.connectsByOutcome.NO_DISPOSITION ?? 0,
    formatHoursMinutes(row.talkSeconds),
    formatHoursMinutes(row.wrapUpSeconds),
    formatHoursMinutes(row.breakSeconds),
    row.callsPerHour.toFixed(1),
    row.voicemails,
    row.medianConnectSeconds != null ? row.medianConnectSeconds.toFixed(1) : "",
    row.abandons,
    formatAbandonRate(row.abandonRate),
  ];
}

export default function DialerReportsPage() {
  const navigate = useNavigate();
  const { data: user } = useGetHomeQuery();
  const membership = user?.activeMembership;
  const isAdmin =
    membership?.status === MembershipStatus.ACTIVE &&
    (membership.role === MembershipRole.OWNER || membership.role === MembershipRole.ADMIN);
  const { data: campaigns = [] } = useGetCampaignsQuery();
  const { data: members = [] } = useGetMembersQuery({ status: MembershipStatus.ACTIVE }, { skip: !isAdmin });
  const { data: phoneNumbers = [] } = useGetOwnPhoneNumbersQuery();
  const [from, setFrom] = useState(daysAgo(DEFAULT_RANGE_DAYS));
  const [to, setTo] = useState(calendarDateIn());
  const [campaignId, setCampaignId] = useState(ALL);
  const [agentId, setAgentId] = useState(ALL);
  const [callerId, setCallerId] = useState(ALL);
  const [audienceSource, setAudienceSource] = useState(ALL);
  const filter: DialerReportFilter = {
    from,
    to,
    campaignId: optional(campaignId),
    agentId: optional(agentId),
    callerId: optional(callerId),
    audienceSource: optional(audienceSource),
  };
  const hasRange = Boolean(from && to);
  const { data: report, isFetching: isLoadingReport } = useGetDialerReportQuery(filter, { skip: !hasRange });
  const { data: sessions = [] } = useGetDialerSessionHistoryQuery(filter, { skip: !hasRange });
  const { data: abandonment } = useGetAbandonmentReportQuery(campaignId, { skip: campaignId === ALL });
  const dialerCampaigns = campaigns.filter((campaign) => campaign.type !== CampaignType.SEQUENCE);
  const agentChoices = members.flatMap((member) =>
    member.user.id
      ? [{ id: member.user.id, name: memberName(member, member.user.email) }]
      : [],
  );

  const handleDownloadReport = () => {
    if (!report) {
      return;
    }
    downloadCsv(`dialer-report-${from}-to-${to}.csv`, [
      REPORT_HEADERS,
      ...report.rows.map(reportCells),
      reportCells(report.totals),
    ]);
  };

  const handleDownloadAbandonment = () => {
    if (!abandonment) {
      return;
    }
    downloadCsv(`abandonment-${abandonment.campaignName ?? abandonment.campaignId}.csv`, [
      ["Window start", "Window end", "Answered by a person", "Abandoned", "Abandonment rate"],
      ...abandonment.windows.map((window) => [
        window.start,
        window.end,
        window.answered,
        window.abandoned,
        formatAbandonRate(window.rate),
      ]),
    ]);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 text-foreground">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/power-dialer")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dialer Reports</h1>
          <p className="text-muted-foreground">
            {isAdmin ? "Every agent's dialing, talk and wrap-up time." : "Your dialing, talk and wrap-up time."}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="grid grid-cols-2 gap-4 pt-6 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="report-from">From</Label>
            <Input id="report-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="report-to">To</Label>
            <Input id="report-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All campaigns</SelectItem>
                {dialerCampaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="space-y-1.5">
              <Label>Agent</Label>
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All agents</SelectItem>
                  {agentChoices.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Caller ID</Label>
            <Select value={callerId} onValueChange={setCallerId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All numbers</SelectItem>
                {phoneNumbers.map((phoneNumber) => (
                  <SelectItem key={phoneNumber.id} value={phoneNumber.id}>{phoneNumber.number}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Audience source</Label>
            <Select value={audienceSource} onValueChange={setAudienceSource}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any source</SelectItem>
                <SelectItem value={HAND_PICKED_AUDIENCE}>Hand-picked leads</SelectItem>
                {Object.values(CampaignTargetType).map((source) => (
                  <SelectItem key={source} value={source}>{AUDIENCE_SOURCE_LABELS[source]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Agents</CardTitle>
            <CardDescription>
              A connect is a bridged call. A conversation is a connect whose disposition counts as connected.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleDownloadReport} disabled={!report}>
            <Download className="mr-2 h-4 w-4" />
            CSV
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoadingReport && !report ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {REPORT_HEADERS.map((header) => (
                    <TableHead key={header} className={header === "Agent" ? undefined : "text-right"}>
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {report?.rows.map((row) => (
                  <TableRow key={row.agentId ?? row.agentName}>
                    {reportCells(row).map((cell, index) => (
                      <TableCell key={REPORT_HEADERS[index]} className={index === 0 ? "font-medium" : "text-right tabular-nums"}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {report && report.rows.length > 1 && (
                  <TableRow className="bg-muted/40">
                    {reportCells(report.totals).map((cell, index) => (
                      <TableCell key={REPORT_HEADERS[index]} className={index === 0 ? "font-semibold" : "text-right font-semibold tabular-nums"}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
                {report && report.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={REPORT_HEADERS.length} className="py-6 text-center text-sm text-muted-foreground">
                      No dialing in this range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {abandonment && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle>Abandonment by 30-day window</CardTitle>
              <CardDescription>
                Calls a person answered that never reached an agent, over all calls a person answered. Goes back up to 5
                years.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadAbandonment}>
              <Download className="mr-2 h-4 w-4" />
              CSV
            </Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Window</TableHead>
                  <TableHead className="text-right">Answered by a person</TableHead>
                  <TableHead className="text-right">Abandoned</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {abandonment.windows.map((window) => (
                  <TableRow key={window.start}>
                    <TableCell>
                      {formatCalendarDate(window.start)} to {formatCalendarDate(window.end)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{window.answered}</TableCell>
                    <TableCell className="text-right tabular-nums">{window.abandoned}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatAbandonRate(window.rate)}</TableCell>
                  </TableRow>
                ))}
                {abandonment.windows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">
                      This campaign has no answered calls yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Sessions</CardTitle>
          <CardDescription>Time in each state for every session in the range, newest first.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Ready</TableHead>
                <TableHead className="text-right">Dialing</TableHead>
                <TableHead className="text-right">On calls</TableHead>
                <TableHead className="text-right">Wrap-up</TableHead>
                <TableHead className="text-right">Break</TableHead>
                <TableHead className="text-right">Dials</TableHead>
                <TableHead className="text-right">Connects</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.sessionId}>
                  <TableCell className="font-medium">{session.agentName}</TableCell>
                  <TableCell>{session.campaignName}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDateTime(session.startedAt)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatHoursMinutes(session.readySeconds)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatHoursMinutes(session.dialingSeconds)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatHoursMinutes(session.connectedSeconds)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatHoursMinutes(session.wrapUpSeconds)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatHoursMinutes(session.breakSeconds)}</TableCell>
                  <TableCell className="text-right tabular-nums">{session.dials}</TableCell>
                  <TableCell className="text-right tabular-nums">{session.connects}</TableCell>
                </TableRow>
              ))}
              {sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="py-6 text-center text-sm text-muted-foreground">
                    No sessions in this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
